import { DecisionTool, type Scenario } from "../../generated/backend/v1/plan";
import { Struct } from "../../generated/google/protobuf/struct";
import type { Family } from "../../generated/core/v1/family";
import { money, parseNumber } from "../lib/format";
import { toolByEnum } from "./tools";

/**
 * Saved what-ifs' titles (QA round 3; Ian: "Add simple auto-titling for the saved what-ifs and make
 * such titles editable"): built here from the tool and its main inputs (the form values kept as the
 * scenario's `inputs`), sent with the scenario, and renamable. Scenarios saved before titles have
 * none and are titled from their inputs the same way.
 */

/** The longest title the backend keeps (`PATCH /api/scenarios/:id`). */
export const TITLE_MAX = 80;

type Inputs = Record<string, unknown>;

const text = (v: unknown): string =>
  typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "";

/** A form value as a number: the forms keep what was typed ("95,000"), older inputs numbers. */
const num = (v: unknown): number => (typeof v === "number" ? v : (parseNumber(text(v)) ?? 0));

const PERIOD_UNIT: Record<string, string> = { weekly: "week", monthly: "month", yearly: "year" };

/** "a month", "every 2 weeks", or "once". */
function howOften(inputs: Inputs): string {
  if (inputs.recurring === false) return "once";
  const unit = PERIOD_UNIT[text(inputs.periodType)] ?? "month";
  const n = Math.max(1, Math.round(num(inputs.period)) || 1);
  return n === 1 ? `a ${unit}` : `every ${n} ${unit}s`;
}

function personName(family: Family | undefined, uuid: unknown): string {
  const i = family?.persons.findIndex((p) => p.uuid === uuid) ?? -1;
  if (!family || i < 0) return "";
  return family.persons[i].givenName.trim() || (i ? "Your partner" : "You");
}

/** "Buy a $650,000 home", or "Buy Cottage for $650,000" when it was named. */
function purchase(inputs: Inputs, price: number, thing: string, defaultName: string): string {
  const name = text(inputs.name);
  if (!price) return name || "";
  return name && name !== defaultName
    ? `Buy ${name} for ${money(price)}`
    : `Buy a ${money(price)} ${thing}`;
}

function jobTitle(inputs: Inputs, family?: Family): string {
  const offers = Array.isArray(inputs.offers) ? (inputs.offers as Inputs[]) : [];
  if (offers.length === 1) {
    const employer = text(offers[0].employer);
    const pay = num(offers[0].income);
    if (!pay) return employer ? `New job at ${employer}` : "A new job";
    return employer ? `New job at ${employer}, ${money(pay)}` : `New job at ${money(pay)}`;
  }
  if (offers.length === 2) {
    return `Two new jobs, ${money(num(offers[0].income))} and ${money(num(offers[1].income))}`;
  }
  if (offers.length > 2) return `${offers.length} new jobs`;
  const existing = Array.isArray(inputs.existing) ? (inputs.existing as Inputs[]) : [];
  const changed = existing.find((j) => j.action === "change" || j.action === "end");
  if (!changed) return "";
  const who = personName(family, changed.personUuid);
  const whose = who === "You" ? "Your" : who ? `${who}’s` : "A";
  return changed.action === "end"
    ? `${whose} job ends`
    : `${whose} pay changes to ${money(num(changed.income))}`;
}

/** What the insured person holds for the event's product, from the wizard's saved rows. */
function heldIn(inputs: Inputs): number {
  const rows = Array.isArray(inputs.people) ? (inputs.people as Inputs[]) : [];
  const r = rows.find((x) => x.personUuid === inputs.personUuid);
  if (!r) return 0;
  return inputs.event === "death"
    ? r.lifeHas === true
      ? num(r.lifeAmount)
      : 0
    : r.ciHas === true
      ? num(r.ciAmount)
      : 0;
}

function insuranceTitle(inputs: Inputs, family?: Family): string {
  const death = inputs.event === "death";
  const who = (family?.persons.length ?? 0) > 1 ? personName(family, inputs.personUuid) : "";
  const event = death
    ? who
      ? who === "You"
        ? "Your death"
        : `Death of ${who}`
      : "Death"
    : who
      ? `Critical illness for ${who}`
      : "Critical illness";
  // The amount tested, else (left blank) what the insured person held, from the wizard's first step.
  const tested = num(inputs.coverage) || num(inputs.individualAmount);
  const coverage = tested || heldIn(inputs);
  if (coverage) return `${event}, ${money(coverage)} coverage`;
  if (death) return event;
  const years = num(inputs.durationYears);
  return years ? `${event}, ${years} year${years === 1 ? "" : "s"} off work` : event;
}

/** The title Abi gives a what-if from its tool and main inputs, at most TITLE_MAX characters. */
export function autoTitle(tool: DecisionTool, inputs: Inputs | undefined, family?: Family): string {
  const v = inputs ?? {};
  let title = "";
  switch (tool) {
    case DecisionTool.EXPENSE: {
      const name = text(v.name);
      const amt = num(v.amount);
      title = amt ? `${name ? `${name}, ` : ""}${money(amt)} ${howOften(v)}` : name;
      break;
    }
    case DecisionTool.HOME:
      title = purchase(v, num(v.price), "home", "New home");
      break;
    case DecisionTool.CAR:
      title = purchase(v, num(v.price), "car", "New car");
      break;
    case DecisionTool.FINANCED_PURCHASE: {
      const name = text(v.name);
      const total = num(v.total);
      title = total
        ? name
          ? `${name}, ${money(total)} financed`
          : `Finance ${money(total)}`
        : name;
      break;
    }
    case DecisionTool.JOB:
      title = jobTitle(v, family);
      break;
    case DecisionTool.INSURANCE:
      title = insuranceTitle(v, family);
      break;
  }
  title = title || toolByEnum(tool)?.title || "A decision";
  return title.length > TITLE_MAX ? `${title.slice(0, TITLE_MAX - 1).trimEnd()}…` : title;
}

/** A saved what-if's title: the one stored (given or renamed), else Abi's from its inputs. */
export function scenarioTitle(s: Scenario, family?: Family): string {
  if (s.title.trim()) return s.title;
  const inputs = s.inputs ? (Struct.toJson(s.inputs) as Inputs) : undefined;
  return autoTitle(s.tool, inputs, family);
}
